import React from "react";
import { motion } from "framer-motion";

// ANIMATION VARIANTS
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } }
};

export default function About() {
  return (
    <div className="text-white bg-gray-950 min-h-screen overflow-hidden">

      {/* HERO SECTION */}
      <section className="relative h-[50vh] flex items-center justify-center text-center px-6 overflow-hidden">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fade}
          transition={{ duration: 1 }}
          className="absolute inset-0 bg-gradient-to-br from-cyan-600/30 to-blue-900/40 backdrop-blur-md"
        />

        {/* Animated Glows */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute top-10 left-16 w-60 h-60 bg-cyan-500/20 blur-3xl rounded-full"
        ></motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute bottom-10 right-16 w-72 h-72 bg-blue-600/20 blur-3xl rounded-full"
        ></motion.div>

        {/* HEADLINE */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 1 }}
          className="relative z-10"
        >
          <h1 className="text-4xl md:text-6xl font-bold">
            About <span className="text-cyan-400">The Kaizen Business Solutions</span>
          </h1>
          <p className="max-w-2xl mx-auto text-gray-300 text-lg mt-4">
            Innovation, technology, and digital growth — all in one place.
          </p>
        </motion.div>
      </section>

      {/* WHO WE ARE */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-10 items-center"
        >
          <motion.div variants={fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Who We <span className="text-cyan-400">Are</span>
            </h2>
            <p className="text-gray-400 leading-relaxed">
              The Kaizen Business Solutions is a technology and digital marketing
              agency dedicated to helping businesses grow, improve efficiency,
              and strengthen their online presence.  
              <br /><br />
              From website development and IT support to SEO and digital
              campaigns, we deliver modern solutions that drive real results.
            </p>
          </motion.div>

          {/* Image with hover animation */}
          <motion.div 
            variants={fadeUp}
            className="relative"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 120 }}
          > 
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-cyan-500/20 blur-2xl rounded-full"></div>
            <motion.img
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
              alt="Team working"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="rounded-2xl shadow-xl border border-gray-800"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* MISSION / VISION / VALUES */}
      <section className="bg-gray-900/40 py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10 text-center">
          {[
            { title: "Mission", desc: "To deliver high-quality..." },
            { title: "Vision", desc: "To become a leading..." },
            { title: "Values", desc: "Innovation, transparency..." }
          ].map((box, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="p-8 bg-gray-900/60 border border-gray-800 rounded-xl shadow-xl hover:shadow-cyan-500/10 transition"
            >
              <h3 className="text-2xl font-bold text-cyan-400 mb-4">{box.title}</h3>
              <p className="text-gray-400">{box.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.h2 
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 0.7 }}
          className="text-3xl md:text-4xl font-bold text-center"
        >
          Why Clients <span className="text-cyan-400">Choose Us</span>
        </motion.h2>

        <motion.p 
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-gray-400 text-center mt-4 max-w-2xl mx-auto"
        >
          We combine technology, strategy, and experience to deliver solutions that matter.
        </motion.p>

        <div className="grid md:grid-cols-3 gap-10 mt-16">
          {[
            "Professional and reliable IT support.",
            "Modern web development with excellent performance.",
            "Digital campaigns designed for real results.",
            "Transparent communication and personalized service.",
            "Fast troubleshooting and remote support.",
            "Continuous improvement in every project (Kaizen philosophy)."
          ].map((item, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-6 bg-gray-900/60 border border-gray-800 rounded-xl hover:-translate-y-1 hover:shadow-cyan-500/20 transition-all"
            >
              <p className="text-gray-300">{item}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-gray-900/50">
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10 text-center"
        >
          {[
            { number: "3+", label: "Years of Experience" },
            { number: "20+", label: "Projects Delivered" },
            { number: "98%", label: "Client Satisfaction" },
            { number: "24/7", label: "Support Availability" }
          ].map((s, i) => (
            <motion.div key={i} variants={fadeUp}>
              <h3 className="text-4xl font-bold text-cyan-400">{s.number}</h3>
              <p className="text-gray-400 mt-2">{s.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center bg-gradient-to-b from-gray-900 to-gray-950">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 0.7 }}
          className="text-3xl md:text-5xl font-bold"
        >
          Let’s Build Something Great Together
        </motion.h2>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-gray-400 mt-4 max-w-xl mx-auto"
        >
          Whether you need a website, IT support, SEO, or digital marketing — we are here to help.
        </motion.p>

        <motion.a
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 0.7, delay: 0.4 }}
          href="/contact"
          className="inline-block mt-8 px-10 py-4 bg-cyan-500 text-gray-900 font-semibold rounded-xl hover:bg-cyan-400 transition-all shadow-lg"
        >
          Request a Quote
        </motion.a>
      </section>
    </div>
  );
}
